/**
 * Compact dropdown selector for Situation categories.
 *
 * The earlier flat chip-row wrapped to 3 lines on mobile (375px) once we
 * had 9 categories + 'all'; pattern ported from the JLPT app's
 * ScenariosCategoryPicker. A single trigger pill opens a popover listbox —
 * saves vertical space, scales cleanly to N categories, and stops layout
 * reflow as counts change.
 *
 * Listbox semantics: `role="option"` and `aria-selected` live on the
 * focusable element (the inner button) — flat structure, no `<li>`
 * wrappers — so screen readers announce selection state correctly.
 *
 * Keeps the props-based API (active / counts / onSelect) used by the rest
 * of the Situations module — no signal coupling.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';

import {
  CATEGORY_LABELS,
  SITUATION_CATEGORIES,
  type SituationCategory
} from './types.js';

type CategoryOption = SituationCategory | 'all';

interface Props {
  active: CategoryOption;
  counts: Record<SituationCategory, number>;
  onSelect: (category: CategoryOption) => void;
}

// Framer Motion's strict Variants type rejects nested `transition` keys
// at the TS level even though they work at runtime — same workaround the
// JLPT picker uses.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const menuVariants: any = {
  closed: { opacity: 0, y: -4, transition: { duration: 0.12 } },
  open: { opacity: 1, y: 0, transition: { duration: 0.16 } }
};

export function SituationsCategoryPicker({ active, counts, onSelect }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);
  const options: CategoryOption[] = ['all', ...SITUATION_CATEGORIES];

  const labelFor = (cat: CategoryOption): string =>
    cat === 'all' ? 'All categories' : CATEGORY_LABELS[cat];
  const countFor = (cat: CategoryOption): number =>
    cat === 'all' ? totalCount : counts[cat];

  useOutsideClose(containerRef, isOpen, () => setIsOpen(false));
  useEscClose(isOpen, () => setIsOpen(false));

  function handleSelect(cat: CategoryOption): void {
    onSelect(cat);
    setIsOpen(false);
  }

  const triggerLabel = `${labelFor(active)} · ${countFor(active)}`;

  return (
    <div ref={containerRef} className="relative mb-6 mx-auto w-full max-w-xs">
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="w-full min-h-[44px] px-4 py-2.5 rounded-full text-sm font-semibold flex items-center justify-between gap-2 bg-white text-slate-700 border border-slate-200 hover:border-slate-300 shadow-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Category filter — currently ${labelFor(active)}, ${countFor(active)} cards`}
      >
        <span className="truncate">{triggerLabel}</span>
        <Chevron isOpen={isOpen} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="menu"
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="absolute z-30 mt-2 w-full bg-white border border-slate-200 rounded-2xl shadow-2 py-1 max-h-[60vh] overflow-y-auto focus:outline-none"
            role="listbox"
            aria-label="Situation categories"
          >
            {options.map(cat => {
              const isActive = cat === active;
              const count = countFor(cat);
              const dimmed = count === 0;

              return (
                <button
                  key={cat}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(cat)}
                  className={[
                    'w-full min-h-[44px] px-4 py-2.5 flex items-center gap-2 text-left text-sm transition-colors focus:outline-none focus-visible:bg-slate-100',
                    isActive
                      ? 'bg-primary-50 text-primary-900 font-semibold'
                      : dimmed
                        ? 'text-slate-400 hover:bg-slate-50'
                        : 'text-slate-700 hover:bg-slate-50'
                  ].join(' ')}
                >
                  <span className="w-4 flex-shrink-0 text-primary-700" aria-hidden="true">
                    {isActive ? '✓' : ''}
                  </span>
                  <span className="flex-1 truncate">{labelFor(cat)}</span>
                  <span
                    className={[
                      'flex-shrink-0 text-xs tabular-nums',
                      isActive ? 'text-primary-700' : 'text-slate-500'
                    ].join(' ')}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// Chevron — inline SVG so stroke weight matches the 14px text
// ============================================

function Chevron({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className={`flex-shrink-0 w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

// ============================================
// Local hooks — outside-click + ESC close
// ============================================
//
// Inlined rather than promoted to src/hooks/ — they're picker-specific
// and short. Promote when a second component needs them.

interface NodeRef {
  current: HTMLElement | null;
}

function useOutsideClose(
  ref: NodeRef,
  enabled: boolean,
  onClose: () => void
): void {
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && !ref.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [enabled, onClose, ref]);
}

function useEscClose(enabled: boolean, onClose: () => void): void {
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [enabled, onClose]);
}
